import { sanityFetch } from './sanity'
import { Project, Experience, Technology, Education, BlogPost, SiteSettings } from '@/types'
import { cached, browserCache } from './cache'

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Wrapper function to handle API errors consistently
 */
async function fetchWithErrorHandling<T>(fetchFn: () => Promise<T>): Promise<T> {
  try {
    return await fetchFn();
  } catch (error) {
    console.error('API Error:', error);

    // Simple error handling to avoid TypeScript issues
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new ApiError(`Failed to fetch data: ${errorMessage}`);
  }
}

/**
 * Fetch all projects
 */
export const getAllProjects = cached(
  async (): Promise<Project[]> => {
    return fetchWithErrorHandling(async () => {
      const projects = await sanityFetch<Project[]>({
        query: `*[_type == "project"] | order(startDate desc) {
          _id,
          title,
          slug,
          description,
          featuredImage,
          "technologies": technologies[]->{ _id, name, category },
          projectType,
          role,
          startDate,
          endDate,
          demoUrl,
          githubUrl,
          blockchainDetails {
            "contractAddresses": contractAddresses[] {
              address,
              network,
              explorerUrl
            },
            tokenInfo {
              name,
              symbol,
              standard
            }
          }
        }`,
        tags: ['project'],
      })

      return projects || []
    })
  },
  {
    ttl: 10 * 60 * 1000, // 10 minutes
    keyGenerator: () => 'projects_all',
    cache: browserCache
  }
)

/**
 * Fetch a single project by slug
 */
export const getProjectBySlug = cached(
  async (slug: string): Promise<Project> => {
    return fetchWithErrorHandling(async () => {
      const project = await sanityFetch<Project>({
        query: `*[_type == "project" && slug.current == $slug][0] {
          _id,
          title,
          slug,
          description,
          featuredImage,
          galleryImages,
          "technologies": technologies[]->{ _id, name, category },
          projectType,
          role,
          startDate,
          endDate,
          demoUrl,
          githubUrl,
          blockchainDetails {
            "contractAddresses": contractAddresses[] {
              address,
              network,
              explorerUrl
            },
            tokenInfo {
              name,
              symbol,
              standard
            }
          }
        }`,
        params: { slug },
        tags: ['project', slug],
      })

      if (!project) {
        throw new ApiError(`Project with slug "${slug}" not found`, 404)
      }

      return project
    })
  },
  {
    ttl: 15 * 60 * 1000, // 15 minutes
    keyGenerator: (slug) => `project_${slug}`,
    cache: browserCache
  }
)

/**
 * Fetch all experiences
 */
export const getAllExperiences = cached(
  async (): Promise<Experience[]> => {
    return fetchWithErrorHandling(async () => {
      const experiences = await sanityFetch<Experience[]>({
        query: `*[_type == "experience"] | order(startDate desc) {
          _id,
          company,
          role,
          startDate,
          endDate,
          description,
          technologies[]->{ _id, name, category },
          achievements,
          location,
          isBlockchainRelated,
          order
        }`,
        tags: ['experience'],
      })

      return experiences || []
    })
  },
  {
    ttl: 15 * 60 * 1000, // 15 minutes
    keyGenerator: () => 'experiences_all',
    cache: browserCache
  }
)

/**
 * Fetch all technologies/skills
 */
export async function getAllTechnologies(): Promise<Technology[]> {
  return fetchWithErrorHandling(async () => {
    const technologies = await sanityFetch<Technology[]>({
      query: `*[_type == "technology"] | order(category) {
        _id,
        name,
        category,
        icon,
        proficiencyLevel,
        yearsOfExperience,
        isBlockchainSpecific,
        blockchainDetails {
          blockchainType,
          networks,
          description
        },
        showcaseProjects[]->{ _id, title, slug }
      }`,
      tags: ['technology'],
    })

    return technologies || []
  })
}

/**
 * Fetch all education entries
 */
export async function getAllEducation(): Promise<Education[]> {
  return fetchWithErrorHandling(async () => {
    const education = await sanityFetch<Education[]>({
      query: `*[_type == "education"] | order(startDate desc) {
        _id,
        institution,
        degree,
        fieldOfStudy,
        startDate,
        endDate,
        description,
        location,
        grade,
        certificateUrl,
        isBlockchainRelated,
        skills[]->{ _id, name, category },
        order
      }`,
      tags: ['education'],
    })

    return education || []
  })
}

/**
 * Fetch all blog posts
 */
export async function getAllBlogPosts(): Promise<BlogPost[]> {
  return fetchWithErrorHandling(async () => {
    const posts = await sanityFetch<BlogPost[]>({
      query: `*[_type == "blogPost"] | order(publishedAt desc) {
        _id,
        title,
        slug,
        excerpt,
        featuredImage,
        publishedAt,
        categories
      }`,
      tags: ['blogPost'],
    })

    return posts || []
  })
}

/**
 * Fetch a single blog post by slug
 */
export async function getBlogPostBySlug(slug: string): Promise<BlogPost> {
  return fetchWithErrorHandling(async () => {
    const post = await sanityFetch<BlogPost>({
      query: `*[_type == "blogPost" && slug.current == $slug][0] {
        _id,
        title,
        slug,
        author->{name, image},
        featuredImage,
        excerpt,
        body,
        categories,
        publishedAt,
        "relatedProjects": relatedProjects[]->{ _id, title, slug }
      }`,
      params: { slug },
      tags: ['blogPost', slug],
    })

    if (!post) {
      throw new ApiError(`Blog post with slug "${slug}" not found`, 404)
    }

    return post
  })
}

/**
 * Fetch site settings
 */
export const getSiteSettings = cached(
  async (): Promise<SiteSettings> => {
    return fetchWithErrorHandling(async () => {
      const settings = await sanityFetch<SiteSettings>({
        query: `*[_type == "siteSettings"][0] {
          _id,
          _type,
          title,
          description,
          keywords,
          author,
          email,
          profileImage {
            asset->{
              _id,
              url
            },
            alt
          },
          logo {
            asset->{
              _id,
              url
            },
            alt
          },
          "socialLinks": socialLinks[] {
            platform,
            url,
            displayText
          },
          "blockchainProfiles": blockchainProfiles[] {
            network,
            identifier,
            displayName,
            url
          },
          resumeFile {
            asset->{
              _id,
              url
            }
          },
          "navigation": navigation[] {
            text,
            path,
            order,
            external
          },
          footerText,
          defaultTheme,
          googleAnalyticsId
        }`,
        tags: ['siteSettings'],
      })

      if (!settings) {
        throw new ApiError('Site settings not found', 404)
      }

      return settings
    })
  },
  {
    ttl: 30 * 60 * 1000, // 30 minutes (settings change less frequently)
    keyGenerator: () => 'site_settings',
    cache: browserCache
  }
)
/**
 *
 Fetch featured projects (for homepage)
 */
export async function getFeaturedProjects(limit = 3): Promise<Project[]> {
  return fetchWithErrorHandling(async () => {
    const projects = await sanityFetch<Project[]>({
      query: `*[_type == "project" && defined(isFeatured) && isFeatured == true] | order(orderRank) [0...$limit] {
        _id,
        title,
        slug,
        description,
        featuredImage,
        technologies[]->{ _id, name, category },
        projectType,
        role,
        startDate,
        endDate,
        demoUrl,
        githubUrl
      }`,
      params: { limit },
      tags: ['project', 'featured'],
    })

    return projects || []
  })
}

/**
 * Fetch projects by technology
 */
export async function getProjectsByTechnology(technologyId: string): Promise<Project[]> {
  return fetchWithErrorHandling(async () => {
    const projects = await sanityFetch<Project[]>({
      query: `*[_type == "project" && $technologyId in technologies[]._ref] | order(orderRank) {
        _id,
        title,
        slug,
        description,
        featuredImage,
        technologies[]->{ _id, name, category },
        projectType,
        role,
        startDate,
        endDate,
        demoUrl,
        githubUrl
      }`,
      params: { technologyId },
      tags: ['project', technologyId],
    })

    return projects || []
  })
}

/**
 * Fetch projects by type
 */
export async function getProjectsByType(projectType: string): Promise<Project[]> {
  return fetchWithErrorHandling(async () => {
    const projects = await sanityFetch<Project[]>({
      query: `*[_type == "project" && projectType == $projectType] | order(orderRank) {
        _id,
        title,
        slug,
        description,
        featuredImage,
        technologies[]->{ _id, name, category },
        projectType,
        role,
        startDate,
        endDate,
        demoUrl,
        githubUrl
      }`,
      params: { projectType },
      tags: ['project', projectType],
    })

    return projects || []
  })
}

/**
 * Fetch blockchain-specific experiences
 */
export async function getBlockchainExperiences(): Promise<Experience[]> {
  return fetchWithErrorHandling(async () => {
    const experiences = await sanityFetch<Experience[]>({
      query: `*[_type == "experience" && defined(isBlockchainRelated) && isBlockchainRelated == true] | order(startDate desc) {
        _id,
        company,
        role,
        startDate,
        endDate,
        description,
        technologies[]->{ _id, name, category },
        achievements,
        location,
        isBlockchainRelated,
        order
      }`,
      tags: ['experience', 'blockchain'],
    })

    return experiences || []
  })
}

/**
 * Fetch technologies by category
 */
export async function getTechnologiesByCategory(category: string): Promise<Technology[]> {
  return fetchWithErrorHandling(async () => {
    const technologies = await sanityFetch<Technology[]>({
      query: `*[_type == "technology" && category == $category] | order(proficiencyLevel desc) {
        _id,
        name,
        category,
        icon,
        proficiencyLevel,
        yearsOfExperience,
        isBlockchainSpecific,
        blockchainDetails {
          blockchainType,
          networks,
          description
        }
      }`,
      params: { category },
      tags: ['technology', category],
    })

    return technologies || []
  })
}

/**
 * Fetch blockchain-specific technologies
 */
export async function getBlockchainTechnologies(): Promise<Technology[]> {
  return fetchWithErrorHandling(async () => {
    const technologies = await sanityFetch<Technology[]>({
      query: `*[_type == "technology" && defined(isBlockchainSpecific) && isBlockchainSpecific == true] | order(proficiencyLevel desc) {
        _id,
        name,
        category,
        icon,
        proficiencyLevel,
        yearsOfExperience,
        isBlockchainSpecific,
        blockchainDetails {
          blockchainType,
          networks,
          description
        }
      }`,
      tags: ['technology', 'blockchain'],
    })

    return technologies || []
  })
}

/**
 * Fetch blog posts by category
 */
export async function getBlogPostsByCategory(category: string): Promise<BlogPost[]> {
  return fetchWithErrorHandling(async () => {
    const posts = await sanityFetch<BlogPost[]>({
      query: `*[_type == "blogPost" && $category in categories] | order(publishedAt desc) {
        _id,
        title,
        slug,
        excerpt,
        featuredImage,
        publishedAt,
        categories
      }`,
      params: { category },
      tags: ['blogPost', category],
    })

    return posts || []
  })
}

/**
 * Search projects by query
 */
export async function searchProjects(query: string): Promise<Project[]> {
  return fetchWithErrorHandling(async () => {
    const projects = await sanityFetch<Project[]>({
      query: `*[_type == "project" && (title match $query || description[] match $query || projectType match $query)] | order(orderRank) {
        _id,
        title,
        slug,
        description,
        featuredImage,
        technologies[]->{ _id, name, category },
        projectType,
        role,
        startDate,
        endDate,
        demoUrl,
        githubUrl
      }`,
      params: { query: `*${query}*` },
      tags: ['project', 'search'],
    })

    return projects || []
  })
}

/**
 * Search blog posts by query
 */
export async function searchBlogPosts(query: string): Promise<BlogPost[]> {
  return fetchWithErrorHandling(async () => {
    const posts = await sanityFetch<BlogPost[]>({
      query: `*[_type == "blogPost" && (title match $query || excerpt match $query || body[] match $query)] | order(publishedAt desc) {
        _id,
        title,
        slug,
        excerpt,
        featuredImage,
        publishedAt,
        categories
      }`,
      params: { query: `*${query}*` },
      tags: ['blogPost', 'search'],
    })

    return posts || []
  })
}